import css from './index.module.css';

import SEO from "@components/seo";
import CartProduct from "@components/cartProduct";
import useUserData from "@hooks/useUserData";

import EmptyCart from "@pages/system/emptyCart";
import MainContent from "@components/content";
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useEffect, useState } from 'react';

export default function Cart() {
  const { userData, cartData, refreshCartData } = useUserData();
  const navigate = useNavigate();
  
  const [address, setAddress] = useState<string>("");
  const [comment, setComment] = useState<string>("");
  const [addresses, setAddresses] = useState<any>([]);
  const [showAddressPopup, setShowAddressPopup] = useState<boolean>(false);

  // Hide floating menu when popup is open
  useEffect(() => {
    if (showAddressPopup) {
      document.body.classList.add('address-popup-open');
    } else {
      document.body.classList.remove('address-popup-open');
    }
    
    return () => {
      document.body.classList.remove('address-popup-open');
    };
  }, [showAddressPopup]);

  const fetchAddresses = () => {
    axios.get(`${import.meta.env.VITE_API_URL}/addresses`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    })
    .then(response => {
      setAddresses(response.data);
    })
    .catch(error => {
      console.log(error);
    });
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  // Refresh addresses when page becomes visible (user returns from account page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchAddresses();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', fetchAddresses);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', fetchAddresses);
    };
  }, []);

  const goToCheckout = (e: any) => {
    e.preventDefault();

    if (!address || !userData.information.name) {
      return toast.error('Заполните организацию и адрес');
    }

    const hasThreeLettersInRow = /[A-Za-zА-Яа-яЁё]{3}/.test(address);
    if (!hasThreeLettersInRow) {
      return toast.error('Адрес должен содержать минимум три подряд идущие буквы');
    }

    const data = {
      shop: address,
      address: address,
      comment: comment
    }

    axios.post(`${import.meta.env.VITE_API_URL}/checkout`, data, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    }).then(response => {
      refreshCartData();
      navigate(`/webapp/order/${response.data.orderID}`);
      toast.success('Заказ оформлен, спасибо');
    }).catch(error => {
      console.log(error);
      if (error.response && error.response.status === 400) {
        toast.error('Некоторых товаров нет на складе');
      } else {
        toast.error('Возникла ошибка');
      }
    });
  }

  return (
  <>
    {(!cartData || cartData.length <= 0) ? 
      <EmptyCart /> :
    
    <MainContent header="Корзина">    
      <SEO 
        title={`Корзина`}
        description={`Корзина`}
        ogImage={``}
        ogUrl={``}
      />

      <div className={css.cart}>
        <Link to={`/webapp`} className={css.back}>Добавить больше товаров</Link>

        <div className={css.productList}>
          {cartData && cartData.map(cart => (
            <CartProduct 
              id={cart.id}
              prodID={cart.product.id}
              image={cart.product.images[0]}
              title={cart.product.name}
              price={cart.product.price}
              unit={cart.product.unit}
              quantity={cart.product.quantity}
              unitCart={cart.unit}
              count={cart.count}
              buttons={true}
            />
          ))}
        </div>

        <div className={css.cartInfo}>
          <div className={css.total}>
            <div className={css.item}>
              <a>Адрес доставки</a>

              <div 
                className={css.addressSelector}
                onClick={() => setShowAddressPopup(true)}
              >
                {address ? (
                  <div className={css.selectedAddress}>
                    <div className={css.selectedAddressText}>
                      {addresses.find((addr: any) => addr.address === address)?.title || address}
                    </div>
                    <div className={css.selectedAddressSubtext}>
                      {addresses.find((addr: any) => addr.address === address)?.address || address}
                    </div>
                  </div>
                ) : (
                  <div className={css.noAddressSelected}>
                    Выберите адрес доставки
                  </div>
                )}
                <div className={css.addressSelectorArrow}>›</div>
              </div>
            </div>

            {showAddressPopup && (
              <div className={css.addressPopup}>
                <div className={css.addressPopupBackdrop} onClick={() => setShowAddressPopup(false)} />
                <div className={css.addressPopupContent}>
                  <div className={css.addressPopupHeader}>
                    <h2>Выберите адрес</h2>
                    <button 
                      className={css.addressPopupClose}
                      onClick={() => setShowAddressPopup(false)}
                    >
                      ×
                    </button>
                  </div>
                  
                  <div className={css.addressPopupList}>
                    {addresses.length > 0 ? (
                      addresses.map((addr: any) => (
                        <div
                          key={addr.id}
                          className={`${css.addressPopupCard} ${address === addr.address ? css.addressPopupCardActive : ''}`}
                          onClick={() => {
                            setAddress(addr.address);
                            setShowAddressPopup(false);
                          }}
                        >
                          <div className={css.addressPopupCardContent}>
                            <div className={css.addressPopupCardTitle}>{addr.title}</div>
                            <div className={css.addressPopupCardAddress}>{addr.address}</div>
                          </div>
                          {address === addr.address && (
                            <div className={css.addressPopupCardCheck}>✓</div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className={css.noAddressesMessage}>
                        У вас нет сохраненных адресов
                      </div>
                    )}
                    
                    <button
                      type="button"
                      className={css.createAddressBtn}
                      onClick={() => {
                        setShowAddressPopup(false);
                        navigate('/webapp/user', { state: { page: 4 } });
                      }}
                    >
                      + Создать адрес
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className={css.item}>
              <a>Комментарий к заказу</a>
              <input type='text' name='comment' value={comment} onChange={(e) => setComment(e.target.value)} placeholder='Комментарий к заказу' />
            </div>

            <a>Это предварительный расчет. Менеджер свяжется с вами для подтверждения заказа и согласования финальной стоимости.</a>
          </div>

          <button className={css.checkout} onClick={goToCheckout}>Оформить заказ</button>
        </div>
      </div>
    </MainContent>
    }
    </>
  )
}